"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Magnetic from "./ui/Magnetic";

interface FlowStep {
  name: string;
  desc: string;
}

interface SystemFlow {
  id: string;
  title: string;
  concept: string;
  description: string;
  steps: FlowStep[];
  diagram: React.ReactNode;
}

export default function SystemDesign() {
  const [activeTab, setActiveTab] = useState<string>("caching");

  const flows: SystemFlow[] = [
    {
      id: "caching",
      title: "Cache-Aside Logic",
      concept: "Database Bypass Architecture",
      description: "How in-memory caching layers mitigate disk-bound database queries, ensuring sub-5ms latency for frequent requests.",
      steps: [
        { name: "Client Request", desc: "User triggers query for resource." },
        { name: "Cache Check", desc: "Express requests the Redis cache using key hashes." },
        { name: "Cache HIT", desc: "If record exists, Redis returns payload in <2ms, bypassing DB." },
        { name: "Cache MISS", desc: "If empty, Express queries MySQL database directly (~60ms)." },
        { name: "Write-back Cache", desc: "Express writes database result to Redis with TTL set to 3600s, returning output to client." }
      ],
      diagram: (
        <div className="flex flex-col md:flex-row items-center justify-around h-full gap-6 p-4">
          {/* User Node */}
          <div className="flex flex-col items-center p-4 bg-white/5 border border-white/10 rounded-2xl w-24 shrink-0 text-center font-mono">
            <i className="fi fi-rr-user text-xl text-indigo-400 mb-1" />
            <span className="text-[10px] text-white">Client</span>
          </div>

          <div className="text-gray-600 flex flex-col items-center">
            <span className="text-[9px] text-indigo-300 font-mono">1. Query</span>
            <svg className="w-12 h-6 md:w-16 md:h-6 rotate-90 md:rotate-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>

          {/* Proxy/Express Node */}
          <div className="flex flex-col items-center p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-2xl w-28 shrink-0 text-center font-mono">
            <i className="fi fi-ts-api-cloud text-indigo-300 mb-1" />
            <span className="text-[10px] text-white font-bold">API Backend</span>
          </div>

          {/* Parallel paths: Redis Cache vs MySQL DB */}
          <div className="flex flex-col gap-8 justify-center items-center w-full">
            {/* Redis Path */}
            <div className="flex items-center gap-4 w-full justify-start pl-4 md:pl-10">
              <div className="text-emerald-500 flex flex-col items-center shrink-0">
                <span className="text-[8px] font-mono">HIT: 2ms</span>
                <svg className="w-8 h-4 rotate-90 md:rotate-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl font-mono text-[9px]">
                <i className="fi fi-rr-bolt text-emerald-400" />
                <div>
                  <span className="text-white font-bold block">Redis Cache</span>
                  <span className="text-gray-500 font-mono">Read Fast</span>
                </div>
              </div>
            </div>

            {/* MySQL Path */}
            <div className="flex items-center gap-4 w-full justify-start pl-4 md:pl-10">
              <div className="text-amber-500 flex flex-col items-center shrink-0">
                <span className="text-[8px] font-mono">MISS: 80ms</span>
                <svg className="w-8 h-4 rotate-90 md:rotate-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl font-mono text-[9px]">
                <i className="fi fi-rr-database text-amber-400" />
                <div>
                  <span className="text-white font-bold block">MySQL DB</span>
                  <span className="text-gray-500 font-mono">Fetch & Cache</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "auth",
      title: "Stateful JWT Flow",
      concept: "Secure Authentication Ingress",
      description: "How identity credentials get verified, tokens encoded, and delivered via HttpOnly Cookies to block Cross-Site Scripting (XSS) interceptors.",
      steps: [
        { name: "Authentication", desc: "User submits username and password over HTTPS." },
        { name: "Cryptographic Sign", desc: "Server validates credentials, signs a HMAC SHA256 JWT payload." },
        { name: "HttpOnly Cookie", desc: "Server sets JWT inside a Cookie with Secure, SameSite=Strict and HttpOnly parameters." },
        { name: "Subsequent Requests", desc: "Browser automatically attaches Cookie on same-domain route requests." },
        { name: "Gatekeeper Middleware", desc: "Reverse proxy parses cookie and parses JWT signature before routing to core services." }
      ],
      diagram: (
        <div className="flex flex-col h-full justify-center gap-4 font-mono text-[10px] p-2">
          {/* Step 1 */}
          <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/5">
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center font-bold text-indigo-300">1</span>
            <div className="flex-1">
              <span className="text-white font-bold block uppercase tracking-wider">Client Login Ingress</span>
              <p className="text-gray-500 text-[9px] mt-0.5">POST /api/auth/login → Payload: User Credentials</p>
            </div>
          </div>
          {/* Arrow */}
          <div className="h-4 border-l border-dashed border-indigo-500/30 ml-6" />
          {/* Step 2 */}
          <div className="flex items-center gap-4 bg-indigo-500/5 p-3 rounded-xl border border-indigo-500/20">
            <span className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center font-bold text-indigo-400">2</span>
            <div className="flex-1">
              <span className="text-indigo-300 font-bold block uppercase tracking-wider">HS256 Token Encryption</span>
              <p className="text-gray-400 text-[9px] mt-0.5">jwt.sign(payload, secret) → Set-Cookie: token=[jwt_data]; HttpOnly; Secure;</p>
            </div>
          </div>
          {/* Arrow */}
          <div className="h-4 border-l border-dashed border-indigo-500/30 ml-6" />
          {/* Step 3 */}
          <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/5">
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center font-bold text-indigo-300">3</span>
            <div className="flex-1">
              <span className="text-white font-bold block uppercase tracking-wider">Subsequent Validation Ingress</span>
              <p className="text-gray-500 text-[9px] mt-0.5">Req Cookie parsed at API Ingress Router. Authorized validation is direct.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "scaling",
      title: "Real-time WebSocket Scaling",
      concept: "Sticky Sessions & Redis Pub/Sub Nodes Coordination",
      description: "Resolving message routing limitations when horizontal scaling drops connections across multiple socket hosts.",
      steps: [
        { name: "Handshake Ingress", desc: "Client attempts socket handshake." },
        { name: "Load Balancer Route", desc: "Traefik maps connection to Node Container A." },
        { name: "Alternative Route", desc: "Recipient Client is connected to Node Container B." },
        { name: "Message Publish", desc: "Container A publishes outgoing socket payload to Redis Pub/Sub channel." },
        { name: "Message Coordinate", desc: "Redis forwards message payload to Container B which forwards socket packet to recipient Client." }
      ],
      diagram: (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-4 font-mono text-[9px]">
          {/* Sockets clients */}
          <div className="flex justify-between w-full max-w-sm">
            <div className="p-2 border border-white/10 rounded bg-white/5 text-white">Client 1 (Connected to A)</div>
            <div className="p-2 border border-white/10 rounded bg-white/5 text-white">Client 2 (Connected to B)</div>
          </div>

          <div className="flex justify-between w-full max-w-xs text-indigo-400">
            <div className="text-center font-bold animate-pulse">WS Connection</div>
            <div className="text-center font-bold animate-pulse">WS Connection</div>
          </div>

          {/* Nodes */}
          <div className="flex justify-between w-full max-w-sm">
            <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-center w-28 text-white font-bold">
              Host Node A
            </div>
            <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-center w-28 text-white font-bold">
              Host Node B
            </div>
          </div>

          {/* Coordination arrow */}
          <div className="h-6 border-l border-dashed border-indigo-500/30" />

          {/* Redis PubSub */}
          <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-2xl w-full text-center flex items-center justify-center gap-2">
            <i className="fi fi-rr-bolt text-purple-400 text-sm animate-pulse" />
            <span className="text-white font-bold uppercase tracking-wider">Redis Pub/Sub Coordination Backplane</span>
          </div>
        </div>
      )
    }
  ];

  const activeFlow = flows.find((flow) => flow.id === activeTab) || flows[0];

  return (
    <section id="system-design" className="section-pad relative z-10 bg-black/20 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block">System Design</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 font-[var(--font-poppins)] tracking-tighter">
            Architectural <span className="gradient-text">Design</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Interactive blueprints of production flows detailing logic execution, caching strategies, and load distribution nodes.
          </p>
        </motion.div>

        {/* Tab Controllers */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {flows.map((flow) => (
            <Magnetic key={flow.id}>
              <button
                onClick={() => setActiveTab(flow.id)}
                className={`px-5 py-3 rounded-2xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${
                  activeTab === flow.id
                    ? "bg-indigo-500/10 border-indigo-400 text-white shadow-xl shadow-indigo-500/10"
                    : "bg-white/5 border-white/5 text-gray-300 hover:border-white/10 hover:text-white"
                }`}
              >
                {flow.title}
              </button>
            </Magnetic>
          ))}
        </div>

        {/* Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* Flow Steps Left */}
          <div className="lg:col-span-5 flex flex-col justify-center bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-8 md:p-10">
            <span className="text-[10px] font-bold text-indigo-400 uppercase font-mono tracking-widest mb-1.5 block">
              {activeFlow.concept}
            </span>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 font-[var(--font-poppins)] tracking-tight">
              {activeFlow.title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 font-medium">
              {activeFlow.description}
            </p>

            <div className="h-px bg-white/5 my-4" />

            <div className="space-y-4">
              {activeFlow.steps.map((step, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="text-xs font-mono font-bold text-indigo-400 mt-0.5">{idx + 1}.</div>
                  <div>
                    <h4 className="text-white text-xs font-black uppercase tracking-wider">{step.name}</h4>
                    <p className="text-gray-400 text-xs mt-0.5 leading-relaxed font-medium">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Diagram Preview Right */}
          <div className="lg:col-span-7 bg-[#05030e] border border-white/10 rounded-[2.5rem] p-8 md:p-12 overflow-hidden flex flex-col justify-center min-h-[420px] shadow-2xl relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-indigo-500/5 blur-[90px] rounded-full pointer-events-none" />
            <div className="relative z-10 h-full w-full">
              {activeFlow.diagram}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
